import { useRef, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { useDispatch } from 'react-redux'
import { FieldKeysEnum } from '@/constants/field-keys.enum'
import { ERROR_CODE } from '@/constants/legacy/constError'
import ROUTES from '@/constants/legacy/constRoutes'
import { ORDER_VERSION } from '@/constants/order-version'
import { PaymentMethodTypeEnum } from '@/constants/payment-method-type.enum'
import MUTATION_VARIABLES from '@/containers/gqls/base/mutation_variables'
import { useApiMutation, useCustomRouter, useOrderPayment } from '@/containers/hooks'
import { OrderFormType } from '@/containers/hooks/order/order-form-schema'
import { doSetGlobalNoticeModal } from '@/stores/reduxUI'
import { getErrorMessageAndCode } from '@/utils/utilApi'
import { get } from '@/utils/utilCommon'

export const useOrderNew = () => {
  const [hasOrderError, setHasOrderError] = useState(false)
  const ableToShowErrorMessageRef = useRef<boolean>(false)

  const { push } = useCustomRouter()

  const {
    isPaymentInProgress,
    requestCreateTransaction,
    setOrderAddressForIamportParam,
    onPaymentMethodSelect: _onPaymentMethodSelect,
    getPaymentMethod: _getPaymentMethod,
  } = useOrderPayment()

  const dispatch = useDispatch()

  const onApiFailure = (error: ApolloError) => {
    if (!ableToShowErrorMessageRef.current) {
      return
    }
    ableToShowErrorMessageRef.current = false

    setHasOrderError(true)
    const { message, code } = getErrorMessageAndCode(error)

    const _getOnOk = (code: string) => {
      if (code === ERROR_CODE.ERROR_INVALID_CUSTOMER_ADDRESS) {
        return () => {
          push(ROUTES.ORDER.DELIVERY_ADDRESSES)
        }
      }

      return undefined
    }

    dispatch(
      doSetGlobalNoticeModal({
        text: message || '시스템 에러가 발생했습니다.\n나중에 다시 시도해 주세요.',
        buttonType: 'ACTION',
        visible: true,
        onOk: _getOnOk(code),
      })
    )
  }

  const [createOrderMutation] = useApiMutation('createOrder', requestCreateTransaction, onApiFailure)
  const createOrder = async ({
    formValues,
    version,
    method,
  }: {
    formValues: any
    version: keyof typeof ORDER_VERSION
    method?: PaymentMethodTypeEnum
  }) => {
    if (method) {
      onPaymentMethodSelect(method)
    }

    setHasOrderError(false)
    ableToShowErrorMessageRef.current = true

    formValues.shippings.forEach((item: any) => {
      item.orderItems.forEach((item: any) => {
        delete item.orderConfirmMileageRate
        delete item.reviewMileageRate
        delete item.imageReviewMileageRate
        delete item.customerPrice
      })
    })
    const variables = MUTATION_VARIABLES.CREATE_ORDER({ ...formValues, version })
    // @ts-ignore
    await createOrderMutation({ variables })
    setOrderAddressForIamportParam({
      address: get(formValues, [FieldKeysEnum.CustomerAddress, FieldKeysEnum.BasicAddress]),
      postcode: get(formValues, [FieldKeysEnum.CustomerAddress, FieldKeysEnum.PostNumber]),
    })
  }

  const createOrderNew = async (formValues: OrderFormType) => {
    const params = {
      coupon: formValues.coupon,
      customerAddress: formValues.customerAddress,
      isUseAllMileage: formValues.isUseAllMileage,
      mileage: Number(formValues.mileage),
      payMethodType: formValues.payMethodType,
      shippings: formValues.shippings.map((shipping) => ({
        coupon: shipping.shippingCoupon,
        orderItems: shipping.orderItems.map((orderItem) => ({
          coupon: orderItem.coupon,
          productOption: orderItem.productOption,
          productPromotion: orderItem.productPromotion,
          quantity: orderItem.quantity,
        })),
      })),
    }
    createOrder({ formValues: params, version: ORDER_VERSION['V4-ESTIMATE-ORDER'] })
  }

  const onPaymentMethodSelect = (method: PaymentMethodTypeEnum) => {
    _onPaymentMethodSelect(method)
  }

  const getPaymentMethod = () => {
    return _getPaymentMethod()
  }

  return {
    isPaymentInProgress,
    hasOrderError,
    createOrder,
    createOrderNew,
    onPaymentMethodSelect,
    getPaymentMethod,
  }
}
